import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import ConditionFactor from '@/assets/icons/condition_factor.svg?component';
import Growth from '@/assets/icons/growth.svg?component';
import LivingWeight from '@/assets/icons/living_weight.svg?component';
import Sgr from '@/assets/icons/sgr.svg?component';
import { Card } from '@/components/atoms/Card';
import { Caption } from '@/components/atoms/Eyebrow';
import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import TitleIcon, { type TitleIconTone } from '@/components/atoms/TitleIcon/TitleIcon';
import type { SmoltWeightData } from '@/domain/smoltReport';
import { formatDateShortLocalized } from '@/utils/date';
import {
    formatNumberByLocale,
    formatPercentageByLocale,
    parseLocale,
} from '@/utils/localeFormatters';

import styles from './WeightSummaryPanel.module.scss';

const SKELETON = { width: 80, height: 32, borderRadius: 8 } as const;

interface WeightSummaryPanelProps {
    weightData: SmoltWeightData;
    deliveryDate: string;
    lastFeedingDate?: string | null;
    isLoading: boolean;
    /** The PDF has a fixed width, so the panel keeps its two columns whatever the window. */
    isPdfExport: boolean;
}

/**
 * The weight summary: living weight at the starvation (last feeding) date and at delivery, beside
 * growth, SGR and condition factor. A missing value shows a dash, never zero.
 */
export default function WeightSummaryPanel(props: WeightSummaryPanelProps) {
    const { weightData, deliveryDate, lastFeedingDate, isLoading, isPdfExport } = props;
    const { t, i18n } = useTranslation();
    const locale = parseLocale(i18n.language);

    const number = (value: number | null, decimals: number): string | null =>
        value != null ? formatNumberByLocale(value, locale, decimals) : null;

    return (
        <Card
            padding="lg"
            className={classNames(styles.panel, { [styles.responsive]: !isPdfExport })}>
            <div className={styles.livingWeight}>
                <div className={styles.livingWeightTitle}>
                    <TitleIcon icon={LivingWeight} tone="caution" />
                    <Caption as="div">{t('smoltReport.detailView.livingWeightTitle')}</Caption>
                </div>
                <div className={styles.livingWeightValues}>
                    {lastFeedingDate != null && (
                        <WeightFigure
                            value={number(weightData.liveWeightAtLastFeeding, 0)}
                            caption={t('smoltReport.detailView.livingWeightAtLastFeeding', {
                                date: formatDateShortLocalized(lastFeedingDate, locale),
                            })}
                            isLoading={isLoading}
                        />
                    )}
                    <WeightFigure
                        value={number(weightData.liveWeight, 0)}
                        caption={t('smoltReport.detailView.livingWeightAtDelivery', {
                            date: formatDateShortLocalized(deliveryDate, locale),
                        })}
                        isLoading={isLoading}
                    />
                </div>
            </div>
            <div className={styles.sideMetrics}>
                <SideMetric
                    icon={Growth}
                    tone="neutral"
                    label={t('smoltReport.detailView.growthTitle')}
                    value={number(weightData.growth, 2)}
                    unit={t('smoltReport.detailView.growthUnit')}
                    isLoading={isLoading}
                />
                <SideMetric
                    icon={Sgr}
                    tone="warning"
                    label={t('smoltReport.detailView.sgrTitle')}
                    value={
                        weightData.sgr != null
                            ? formatPercentageByLocale(weightData.sgr, locale, 2)
                            : null
                    }
                    isLoading={isLoading}
                />
                <SideMetric
                    icon={ConditionFactor}
                    tone="info"
                    label={t('smoltReport.detailView.conditionFactorTitle')}
                    value={number(weightData.conditionFactor, 2)}
                    isLoading={isLoading}
                />
            </div>
        </Card>
    );
}

interface WeightFigureProps {
    value: string | null;
    caption: string;
    isLoading: boolean;
}

function WeightFigure(props: WeightFigureProps) {
    const { value, caption, isLoading } = props;
    return (
        <div className={styles.figure}>
            {isLoading ? (
                <SkeletonBox {...SKELETON} />
            ) : (
                <div className={styles.figureValue}>
                    {value ?? '—'}
                    {value != null && <span className={styles.figureUnit}>g</span>}
                </div>
            )}
            <div className={styles.figureCaption}>{caption}</div>
        </div>
    );
}

interface SideMetricProps {
    icon: typeof Growth;
    tone: TitleIconTone;
    label: string;
    value: string | null;
    unit?: string;
    isLoading: boolean;
}

function SideMetric(props: SideMetricProps) {
    const { icon, tone, label, value, unit, isLoading } = props;
    return (
        <div className={styles.sideMetric}>
            <TitleIcon icon={icon} tone={tone} />
            <Caption as="div" className={styles.sideMetricLabel}>
                {label}
            </Caption>
            {isLoading ? (
                <SkeletonBox {...SKELETON} />
            ) : (
                <div className={styles.sideMetricValue}>
                    {value ?? '—'}
                    {value != null && unit && <span className={styles.sideMetricUnit}>{unit}</span>}
                </div>
            )}
        </div>
    );
}
